/**
 * Suppliers API - Provider directory and workflow integration
 * @module api/suppliers
 */

import { apiRequest, API_BASE_URL, getAuthHeaders } from './core.js';

// =============================================================================
// SUPPLIER API
// =============================================================================

/**
 * Supplier API - Provider/vendor management
 */
export const supplierAPI = {
  // Categories reference
  getCategories: () => apiRequest('/supplier_categories'),

  // CRUD operations
  getAll: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.category) params.append('category', filters.category);
    if (filters.status) params.append('status', filters.status);
    if (filters.is_preferred !== undefined) params.append('is_preferred', filters.is_preferred);
    if (filters.search) params.append('search', filters.search);
    if (filters.org_id) params.append('org_id', filters.org_id);
    const query = params.toString();
    return apiRequest(`/suppliers${query ? `?${query}` : ''}`);
  },
  getById: (id) => apiRequest(`/suppliers/${id}`),
  create: (supplier) => apiRequest('/suppliers', {
    method: 'POST',
    body: JSON.stringify(supplier),
  }),
  update: (id, supplier) => apiRequest(`/suppliers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(supplier),
  }),
  delete: (id) => apiRequest(`/suppliers/${id}`, {
    method: 'DELETE',
  }),

  // Stats and specialized queries
  getStatsByCategory: (orgId) => {
    const params = new URLSearchParams();
    if (orgId) params.append('org_id', orgId);
    const queryStr = params.toString();
    return apiRequest(`/suppliers/stats/by-category${queryStr ? '?' + queryStr : ''}`);
  },
  getPreferred: (orgId, category) => {
    const params = new URLSearchParams();
    if (orgId) params.append('org_id', orgId);
    if (category) params.append('category', category);
    const queryStr = params.toString();
    return apiRequest(`/suppliers/preferred${queryStr ? '?' + queryStr : ''}`);
  },
  getForWorkflow: (workflowType, orgId) => {
    const params = new URLSearchParams();
    if (orgId) params.append('org_id', orgId);
    const queryStr = params.toString();
    return apiRequest(`/suppliers/for-workflow/${workflowType}${queryStr ? '?' + queryStr : ''}`);
  },

  /**
   * Import suppliers from a .vcf file.
   * Uses raw fetch + FormData (apiRequest forces JSON content-type).
   * @param {File} file - The .vcf file
   * @param {number[]} [selectedIndexes] - Indexes of contacts to import (null = all)
   * @returns {Promise<Object>} { ok, data: { total_parsed, imported, skipped_doublons, errors, details[] } }
   */
  importVCards: async (file, selectedIndexes = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (selectedIndexes) {
      formData.append('selected_indexes', JSON.stringify(selectedIndexes));
    }

    const authHeaders = getAuthHeaders();
    // Do NOT set Content-Type — browser sets multipart boundary automatically
    const response = await fetch(`${API_BASE_URL}/suppliers/import-vcards`, {
      method: 'POST',
      headers: { ...authHeaders },
      body: formData,
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Import failed' }));
      throw new Error(err.error || `HTTP ${response.status}`);
    }

    return response.json();
  },
};

// =============================================================================
// SUPPLIER SERVICES API
// =============================================================================

/**
 * Supplier Service API - Services offered by suppliers
 */
export const supplierServiceAPI = {
  getBySupplier: (supplierId) => apiRequest(`/supplier_services/supplier/${supplierId}`),
  create: (service) => apiRequest('/supplier_services', {
    method: 'POST',
    body: JSON.stringify(service),
  }),
  delete: (id) => apiRequest(`/supplier_services/${id}`, {
    method: 'DELETE',
  }),
};

// =============================================================================
// SUPPLIER MAKE CERTIFICATIONS API
// =============================================================================

/**
 * Supplier Make API - Brand certifications
 */
export const supplierMakeAPI = {
  getBySupplier: (supplierId) => apiRequest(`/supplier_makes/supplier/${supplierId}`),
  create: (make) => apiRequest('/supplier_makes', {
    method: 'POST',
    body: JSON.stringify(make),
  }),
  delete: (id) => apiRequest(`/supplier_makes/${id}`, {
    method: 'DELETE',
  }),
};

// =============================================================================
// WORKFLOW OUTPUT API
// =============================================================================
// SUPPLIER CONTRACTS & SLA
// =============================================================================

export const supplierContractAPI = {
  getAll: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.supplier_id) params.append('supplier_id', filters.supplier_id);
    const query = params.toString();
    return apiRequest(`/supplier-contracts${query ? `?${query}` : ''}`);
  },
  getBySupplier: (supplierId) => apiRequest(`/supplier-contracts/supplier/${supplierId}`),
  getById: (id) => apiRequest(`/supplier-contracts/${id}`),
  create: (contract) => apiRequest('/supplier-contracts', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(contract) }),
  update: (id, contract) => apiRequest(`/supplier-contracts/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(contract) }),
  delete: (id) => apiRequest(`/supplier-contracts/${id}`, { method: 'DELETE' }),
  getExpiring: (days = 30) => apiRequest(`/supplier-contracts/expiring?days=${days}`),
  getSlaSummary: () => apiRequest(`/supplier-contracts/sla-summary`),
};

// =============================================================================

/**
 * Workflow Output API - Workflow action outputs
 */
export const workflowOutputAPI = {
  getByWorkflow: (workflowType) => apiRequest(`/workflow_outputs/${workflowType}`),
  getForTransition: (workflowType, fromStatus, toStatus) =>
    apiRequest(`/workflow_outputs/${workflowType}/transition?from_status=${fromStatus}&to_status=${toStatus}`),
  create: (output) => apiRequest('/workflow_outputs', {
    method: 'POST',
    body: JSON.stringify(output),
  }),
  update: (id, output) => apiRequest(`/workflow_outputs/${id}`, {
    method: 'PUT',
    body: JSON.stringify(output),
  }),
  delete: (id) => apiRequest(`/workflow_outputs/${id}`, {
    method: 'DELETE',
  }),
};

// =============================================================================
// WORKFLOW ACTION API
// =============================================================================

/**
 * Workflow Action API - Audit trail
 */
export const workflowActionAPI = {
  getRecent: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.workflow_type) params.append('workflow_type', filters.workflow_type);
    if (filters.triggered_by) params.append('triggered_by', filters.triggered_by);
    if (filters.limit) params.append('limit', filters.limit);
    const query = params.toString();
    return apiRequest(`/workflow_actions${query ? `?${query}` : ''}`);
  },
  getByEntity: (workflowType, entityId) => apiRequest(`/workflow_actions/${workflowType}/${entityId}`),
  record: (action) => apiRequest('/workflow_actions', {
    method: 'POST',
    body: JSON.stringify(action),
  }),
};
